import axios from "axios";
import { DataProvider, Identifier, SortPayload } from "react-admin";
import {
  FinlandCommunicationAssetLevelEnum,
  FinlandCommunicationFieldTypeEnum,
  FinlandCommunicationServiceOperatorEnum,
} from "shared/enums/globalEnums";
import {
  FinlandCommunicationResourcesEnum,
  getRawUrlBase,
  PlanningResourcesEnum,
  SiteResourcesEnum,
} from "shared/enums/resourceMappings";
import {
  AssociationCreatePayload,
  FinlandConfig,
  PlanningMask,
  SelectedGroup,
  SelectedSite,
  SiteMetricPayload,
} from "../types";

export const fetchSelectedSitesQuery = async (
  dataprovider: DataProvider,
  selectedSitesIds: number[],
): Promise<SelectedSite[]> => {
  const { data } = await dataprovider.getList(SiteResourcesEnum.Sites, {
    sort: { field: "id", order: "ASC" } as SortPayload,
    pagination: { page: 1, perPage: 100 },
    filter: { id__in: selectedSitesIds },
  });

  return data as SelectedSite[];
};

export const fetchConfigsQuery = async (
  dataprovider: DataProvider,
  groupCode: Identifier,
): Promise<FinlandConfig[]> => {
  const { data: configs } = await dataprovider.getList(FinlandCommunicationResourcesEnum.Configs, {
    sort: { field: "id", order: "ASC" } as SortPayload,
    pagination: { page: 1, perPage: 100 },
    filter: {
      service_operator: FinlandCommunicationServiceOperatorEnum.TSO,
      asset_level: FinlandCommunicationAssetLevelEnum.PROVIDING_ENTITY,
      asset_id: groupCode,
      field_type: FinlandCommunicationFieldTypeEnum.FLOAT,
    },
  });
  return configs as FinlandConfig[];
};

export const fetchSelectedGroupsQuery = async (
  dataprovider: DataProvider,
  groupIds: Identifier[],
): Promise<SelectedGroup[]> => {
  const { data: groups } = await dataprovider.getList(SiteResourcesEnum.Groups, {
    filter: {
      id__in: groupIds,
    },
  });
  return groups as SelectedGroup[];
};

export const fetchSelectedGroupQuery = async (
  dataprovider: DataProvider,
  groupId: Identifier,
): Promise<SelectedGroup> => {
  const { data: group } = await dataprovider.getOne(SiteResourcesEnum.Groups, { id: groupId });
  return group as SelectedGroup;
};

export const fetchPlanningMasksForSpecificDateQuery = async (
  siteIds: Identifier[],
  date: Date | string,
): Promise<PlanningMask[]> => {
  try {
    const start_date = new Date(date);
    const end_date = new Date(date);
    end_date.setDate(start_date.getDate() + 1);
    const params = {
      site_id__in: String(siteIds),
      start_date: start_date.toISOString(),
      end_date: end_date.toISOString(),
      page: 1,
      size: siteIds.length,
    };
    const url = `${getRawUrlBase(PlanningResourcesEnum.PlanningMasks)}trimmed/`;
    const response = await axios.get(url, { params });
    const data = response.data?.items ?? [];
    return data as PlanningMask[];
  } catch (error) {
    console.error("Error fetching planning masks:", error);
    return [];
  }
};

export const sendModbusMetricsQuery = async (metricsPayload: SiteMetricPayload[]) => {
  const response = await axios.post(
    `${getRawUrlBase(FinlandCommunicationResourcesEnum.ModbusSiteMetrics)}many/`,
    metricsPayload,
  );
  if (response.status >= 200 && response.status < 300) {
    return response.data;
  }
  throw new Error(`Failed to create modbus metrics (status: ${response.status})`);
};

export const postAssociationBatchQuery = async (associationValues: AssociationCreatePayload) => {
  const response = await axios.post(`${getRawUrlBase(SiteResourcesEnum.Associations)}batch/`, associationValues);
  if (response.status >= 200 && response.status < 300) {
    return response.data;
  }
  throw new Error(`Failed to create associations (status: ${response.status})`);
};
